<script setup lang="ts">
import ProjectCard from "@/components/Card/ProjectCard.vue";
import { Project, Status } from "@/types";
import {
  computed,
  defineEmits,
  defineProps,
  ref,
  onMounted,
  onUnmounted,
} from "vue";

type Props = {
  statusList: Status[];
  allStatuses: Status[];
  projectList: Project[];
  selectedViewOptions: string[];
  nameFilter: string[];
};

const props = defineProps<Props>();

type Emits = {
  (event: "toggleStatusOpen", statusId: number): void;
};

const emit = defineEmits<Emits>();

const viewOptions = computed(() => [...props.selectedViewOptions]);

const filteredProjectsForStatus = (statusId: number) => {
  return props.projectList.filter((project: Project) => {
    const matchesStatus = project.statusId === statusId;
    const matchesType = viewOptions.value.length
      ? viewOptions.value.includes(project.type)
      : true;
    const matchesName =
      props.nameFilter.length === 0 ||
      props.nameFilter.includes(project.customer.name);

    return matchesStatus && matchesType && matchesName;
  });
};

const screenWidth = ref(window.innerWidth);
const currentPage = ref(0);

// Handle resizing
const updateScreenWidth = () => {
  screenWidth.value = window.innerWidth;
};
onMounted(() => window.addEventListener("resize", updateScreenWidth));
onUnmounted(() => window.removeEventListener("resize", updateScreenWidth));

// Compute how many columns fit per page
const columnsPerPage = computed(() => {
  if (screenWidth.value >= 1400) return 8;
  if (screenWidth.value <= 1399 && screenWidth.value >= 1234) return 7;
  if (screenWidth.value <= 1233 && screenWidth.value >= 1068) return 6;
  if (screenWidth.value <= 1067 && screenWidth.value >= 902) return 5;
  if (screenWidth.value <= 901 && screenWidth.value >= 768) return 4;
  if (screenWidth.value <= 767) return 8;
  return 1;
});

// Compute the total number of pages
const totalPages = computed(() =>
  Math.ceil(
    (props.statusList.length > 0
      ? props.statusList.length
      : props.allStatuses.length) / columnsPerPage.value
  )
);

// Get the current slice of visible columns
const visibleStatuses = computed(() => {
  const list =
    props.statusList && props.statusList.length > 0
      ? props.statusList
      : props.allStatuses;

  const start = currentPage.value * columnsPerPage.value;
  const end = start + columnsPerPage.value;
  return list.slice(start, end);
});

// Navigation
const goNext = () => {
  if (currentPage.value < totalPages.value - 1) currentPage.value++;
};
const goBack = () => {
  if (currentPage.value > 0) currentPage.value--;
};
</script>

<template>
  <div class="pt-[12px] w-full md:w-max">
    <!-- Navigation buttons -->

    <div class="w-full md:w-max">
      <div class="flex mb-[12px] max-md:hidden">
        <button
          v-if="!(currentPage === 0)"
          @click="goBack"
          class="px-[16px] h-[16px] flex items-center justify-center"
        >
          ←
        </button>
        <div class="w-full flex justify-end">
          <button
            v-if="!(currentPage >= totalPages - 1)"
            @click="goNext"
            class="px-[16px] h-[16px] flex items-center justify-center"
          >
            →
          </button>
        </div>
      </div>
      <div class="w-full md:w-max">
        <!-- Project column -->
        <div
          class="flex w-full md:w-max items-start gap-[6px] max-md:flex-col max-md:w-full max-md:gap-[12px] md:px-[12px]"
        >
          <div
            v-for="status in visibleStatuses"
            :key="status.id"
            class="h-fit max-w-[160px] min-w-[160px] max-md:w-full max-md:max-w-[1000px]"
          >
            <div
              class="bg-[#E5E4E2] flex flex-col px-[4.5px] py-[6px] rounded-primary-radius w-full"
            >
              <!-- Column header -->
              <div
                @click="emit('toggleStatusOpen', status.id)"
                class="flex w-full items-center justify-between px-[5px] cursor-pointer"
              >
                <div
                  class="w-6 h-6 flex items-center justify-center bg-[#D3D1CD] p-2 rounded-primary-radius"
                >
                  <span class="text-xs font-medium text-primary-text-color">
                    {{
                      filteredProjectsForStatus(status.id).length
                        ? filteredProjectsForStatus(status.id).length
                        : 0
                    }}
                  </span>
                </div>
                <span
                  class="text-base md:text-xs font-medium text-primary-text-color"
                  >{{ status.name }}</span
                >
                <div>
                  <img
                    class="cursor-pointer"
                    :class="status.isOpen ? 'rotate-180' : '0'"
                    src="@/assets/arrow-caret-up.svg"
                    alt=""
                  />
                </div>
              </div>

              <div
                v-if="status.isOpen"
                class="w-full h-[calc(5*112px)] overflow-y-auto max-md:h-auto max-md:py-[6px]"
              >
                <!-- Project card -->
                <div
                  v-for="project in filteredProjectsForStatus(status.id)"
                  :key="project.id"
                >
                  <div
                    v-if="
                      project.statusId === status.id &&
                      (nameFilter.length === 0 ||
                        nameFilter.includes(project.customer.name))
                    "
                    class="mt-[6px]"
                  >
                    <ProjectCard
                      :project-id="project.id"
                      :customer="project.customer"
                      :name="project.name"
                      :phases="project.phases"
                      :statusId="project.statusId"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
